import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, Star, PlayCircle, Calendar, ListChecks, MessageCircle, ShieldAlert, MonitorOff, Brain, Headphones, Lock, Download, Zap, Smartphone, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { LegalAccordion } from '../ui/LegalAccordion';
const CHECKOUT_URL = 'https://pay.hotmart.com/REPLACE_WITH_MY_CHECKOUT';
export function SalesPage() {
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-130">
      <TransitionSection data-id="element-131" />
      <OfferSection data-id="element-132" />
      <HowItWorksSection data-id="element-133" />
      <TestimonialsSection data-id="element-134" />
      <FAQSection data-id="element-135" />
      <Footer data-id="element-136" />
    </div>;
}
function TransitionSection() {
  const painPoints = [{
    emoji: '😔',
    text: 'Te vas a la cama sintiéndote culpable porque gritaste, aunque prometiste que hoy no lo harías.'
  }, {
    emoji: '⏰',
    text: 'Las mañanas son una batalla: vestirse, desayunar, salir a tiempo... todo termina en tensión.'
  }, {
    emoji: '📱',
    text: 'Cada vez que apagas la tablet o el celular, empieza una guerra. Y a veces cedes solo para tener un momento de paz.'
  }, {
    emoji: '😤',
    text: 'Le explicas las cosas mil veces, pero parece que no escucha. Y cuando insistes, la situación escala.'
  }, {
    emoji: '💔',
    text: 'Amas profundamente a tu hijo, pero hay días en que sientes que no tienes herramientas, que nada funciona.'
  }, {
    emoji: '😩',
    text: 'Llegas al final del día agotado, sin energía, preguntándote si lo estás haciendo bien.'
  }];
  return <div className="bg-brand-dark text-white relative overflow-hidden" data-id="element-137">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-138">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-brand-blue/20 rounded-full blur-3xl" data-id="element-139" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-brand-lavender/20 rounded-full blur-3xl" data-id="element-140" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-green/10 rounded-full blur-3xl" data-id="element-141" />
      </div>

      {/* Empathy Section */}
      <div className="relative z-10 px-5 pt-14 pb-10 max-w-lg mx-auto" data-id="element-142">
        <p className="text-brand-lavender font-bold uppercase tracking-widest text-disclaimer mb-4 text-center" data-id="element-143">
          Sabemos cómo te sientes
        </p>
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-8 text-center" data-id="element-144">
          Si algo de esto te suena familiar, no estás solo.
        </h2>

        <div className="space-y-3 mb-10" data-id="element-145">
          {painPoints.map((point, i) => <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded-2xl flex items-start space-x-3" data-id="element-146">
              <span className="text-xl shrink-0 mt-0.5" data-id="element-147">{point.emoji}</span>
              <p className="text-gray-200 leading-relaxed text-card-body" data-id="element-148">
                {point.text}
              </p>
            </div>)}
        </div>

        <div className="text-center mb-4" data-id="element-149">
          <p className="text-offer-sub font-heading font-bold" data-id="element-150">
            Nada de esto significa que seas mal padre o mala madre.
          </p>
        </div>
        <p className="text-gray-300 text-card-body text-center leading-relaxed" data-id="element-151">
          Significa que necesitas un sistema más simple. Pasos claros. Frases
          concretas. Rutinas que funcionen incluso cuando estás cansado.
        </p>
      </div>

      {/* Divider */}
      <div className="relative z-10 flex justify-center py-4" data-id="element-152">
        <div className="w-14 h-0.5 bg-white/20 rounded-full" data-id="element-153" />
      </div>

      {/* Solution Section */}
      <div className="relative z-10 px-5 pt-6 pb-14 max-w-lg mx-auto text-center" data-id="element-154">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-4 text-center" data-id="element-155">
          Tu familia no necesita más gritos. Necesita un sistema simple.
        </h2>
        <p className="text-offer-sub text-gray-300 mb-8 leading-relaxed" data-id="element-156">
          Cuando un padre está agotado, no necesita teoría complicada. Necesita
          saber qué hacer hoy, qué decir en el momento difícil y cómo repetirlo
          sin sentirse perdido.
        </p>
        <p className="text-gray-400 text-card-body mb-10 leading-relaxed" data-id="element-157">
          Por eso creamos algo diferente. No es un curso largo. No es una app
          para tu hijo. No es más contenido que se acumula sin usar.
        </p>

        <div className="bg-white/10 p-6 rounded-[1.5rem] backdrop-blur-md border border-white/20 text-left shadow-2xl" data-id="element-158">
          <div className="inline-block bg-brand-lavender/20 text-brand-lavender text-disclaimer font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4" data-id="element-159">
            Presentamos
          </div>
          <h3 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-4 text-center" data-id="element-160">
            Calma en Casa
          </h3>
          <p className="text-gray-200 leading-relaxed mb-6 text-card-body text-center" data-id="element-161">
            Un plan guiado de 21 días para padres de niños intensos. Cada día
            recibes una microacción simple de 5 a 10 minutos: una frase, una
            rutina, un paso concreto. Sin teoría. Sin complicaciones. Solo lo
            que necesitas para ese día.
          </p>

          <div className="bg-gradient-to-r from-brand-blue/30 to-brand-blue/10 border border-brand-blue/40 p-4 rounded-2xl relative overflow-hidden" data-id="element-162">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-brand-blue" data-id="element-163" />
            <p className="font-heading font-bold text-white text-card-title mb-1.5 flex items-center" data-id="element-164">
              <MonitorOff className="mr-2.5 text-brand-blue" size={20} data-id="element-165" />
              No es otra pantalla para tu hijo.
            </p>
            <p className="text-blue-100 text-card-body" data-id="element-166">
              Es una guía diaria para que tú sepas qué hacer, incluso en los
              peores momentos.
            </p>
          </div>
        </div>
      </div>
    </div>;
}
function OfferSection() {
  const features = [{
    icon: <Calendar size={20} data-id="element-167" />,
    title: 'Plan guiado paso a paso',
    desc: 'Microacciones diarias de 5 a 10 minutos para aplicar en casa.'
  }, {
    icon: <ShieldAlert size={20} data-id="element-168" />,
    title: 'Herramientas para regular momentos intensos',
    desc: 'Pasos simples para antes, durante y después del conflicto.'
  }, {
    icon: <ListChecks size={20} data-id="element-169" />,
    title: 'Apoyo para rutinas y transiciones',
    desc: 'Plantillas para mañanas, noche y tareas.'
  }, {
    icon: <MessageCircle size={20} data-id="element-170" />,
    title: 'Guías prácticas para padres',
    desc: 'Qué decir cuando hay rabietas, oposición o frustración.'
  }, {
    icon: <Eye size={20} data-id="element-171" />,
    title: 'Recursos visuales y ejercicios simples',
    desc: 'Material descargable para facilitar la comunicación.'
  }, {
    icon: <Smartphone size={20} data-id="element-172" />,
    title: 'Acceso fácil desde el móvil',
    desc: 'Todo el material disponible en cualquier momento desde tu celular.'
  }, {
    icon: <Headphones size={20} data-id="element-173" />,
    title: 'Audios cortos para padres',
    desc: 'Refuerzos breves para ayudarte a mantener la calma.'
  }];
  return <div className="px-5 py-14 bg-gray-50" data-id="element-174">
      <div className="max-w-lg mx-auto" data-id="element-175">
        <div className="text-center mb-10" data-id="element-176">
          <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-4 text-center" data-id="element-177">
            Un plan práctico para traer más calma, estructura y apoyo a tu hogar
          </h2>
          <p className="text-offer-sub text-gray-600 text-center" data-id="element-178">
            Recibe una guía paso a paso para ayudarte a acompañar mejor a tu
            hijo, con herramientas simples, aplicables y pensadas para la vida
            real.
          </p>
        </div>

        {/* Mockup */}
        <div className="bg-white rounded-[1.5rem] shadow-xl border border-gray-100 p-5 mb-12 relative overflow-hidden" data-id="element-179">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-brand-blue/10 to-transparent" data-id="element-180"></div>
          <div className="relative z-10 flex flex-col items-center pt-3 pb-1" data-id="element-181">
            <div className="w-16 h-16 bg-brand-blue text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-brand-blue/30" data-id="element-182">
              <Calendar size={32} data-id="element-183" />
            </div>
            <div className="w-full space-y-3" data-id="element-184">
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-185">
                <div className="flex items-center space-x-3" data-id="element-186">
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green" data-id="element-187">
                    <CheckCircle2 size={16} data-id="element-188" />
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-189">
                    Día 1: La rutina visual
                  </span>
                </div>
                <PlayCircle className="text-gray-300" size={22} data-id="element-190" />
              </div>
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-191">
                <div className="flex items-center space-x-3" data-id="element-192">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100" data-id="element-193">
                    <span className="font-bold text-sm" data-id="element-194">2</span>
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-195">
                    Día 2: Frases de conexión
                  </span>
                </div>
                <PlayCircle className="text-brand-blue" size={22} data-id="element-196" />
              </div>
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between opacity-60" data-id="element-197">
                <div className="flex items-center space-x-3" data-id="element-198">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100" data-id="element-199">
                    <span className="font-bold text-sm" data-id="element-200">3</span>
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-201">
                    Día 3: Transiciones
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-6 text-center" data-id="element-202">
          Lo que incluye el plan:
        </h3>

        <div className="space-y-3.5 mb-12" data-id="element-203">
          {features.map((f, i) => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-start space-x-4 relative overflow-hidden" data-id="element-204">
              <div className="bg-blue-50 p-3 rounded-xl text-brand-blue shrink-0 relative" data-id="element-205">
                {f.icon}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-dark text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" data-id="element-206">
                  {i + 1}
                </div>
              </div>
              <div className="pt-0.5" data-id="element-207">
                <h4 className="font-heading font-bold text-brand-dark mb-1 text-card-title" data-id="element-208">
                  {f.title}
                </h4>
                <p className="text-card-body text-gray-600 leading-relaxed" data-id="element-209">
                  {f.desc}
                </p>
              </div>
            </div>)}
        </div>

        {/* Who this is for */}
        <div className="bg-brand-beige/30 p-6 rounded-2xl border border-brand-beige/50 mb-6" data-id="element-210">
          <h3 className="font-heading font-bold text-brand-dark text-card-title mb-3 text-center" data-id="element-211">
            ¿Para quién es esto?
          </h3>
          <p className="text-card-body text-gray-700 text-center leading-relaxed" data-id="element-212">
            Para padres de niños de 4 a 10 años que quieren más calma en casa,
            mejores rutinas, menos conflictos y apoyo práctico.
          </p>
        </div>

        {/* What this is not */}
        <div className="bg-gray-100 p-6 rounded-2xl border border-gray-200 mb-12" data-id="element-213">
          <h3 className="font-heading font-bold text-gray-800 text-card-title mb-3 text-center" data-id="element-214">
            Lo que NO es
          </h3>
          <p className="text-card-body text-gray-600 text-center leading-relaxed" data-id="element-215">
            Esto no es un diagnóstico médico y no reemplaza una evaluación
            profesional. Es una herramienta educativa y práctica de apoyo para
            padres.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-brand-dark text-white p-6 sm:p-10 rounded-[2rem] text-center shadow-2xl relative overflow-hidden" data-id="element-216">
          <div className="absolute top-5 -right-10 bg-brand-lavender text-brand-dark text-disclaimer font-bold py-1 px-12 transform rotate-45 shadow-md" data-id="element-217">
            OFERTA
          </div>

          <p className="text-brand-lavender font-bold uppercase tracking-widest text-disclaimer mb-4" data-id="element-218">
            Acceso Completo
          </p>
          <div className="flex justify-center items-baseline space-x-2 mb-6" data-id="element-219">
            <span className="text-5xl sm:text-6xl font-heading font-bold" data-id="element-220">
              $27
            </span>
            <span className="text-gray-400 font-medium text-card-body" data-id="element-221">
              USD
            </span>
          </div>

          <div className="flex flex-col space-y-3 mb-8 text-card-body text-gray-300" data-id="element-222">
            <div className="flex items-center justify-center space-x-2.5" data-id="element-223">
              <Lock size={18} className="text-brand-green" data-id="element-224" />
              <span data-id="element-225">Pago 100% seguro y privado</span>
            </div>
            <div className="flex items-center justify-center space-x-2.5" data-id="element-226">
              <Zap size={18} className="text-yellow-400" data-id="element-227" />
              <span data-id="element-228">Acceso inmediato al material</span>
            </div>
            <div className="flex items-center justify-center space-x-2.5" data-id="element-229">
              <Download size={18} className="text-brand-blue" data-id="element-230" />
              <span data-id="element-231">Plantillas descargables incluidas</span>
            </div>
          </div>

          <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-232">
            <Button variant="success" className="w-full shadow-xl shadow-green-900/50" data-id="element-233">
              Quiero mi plan guiado
            </Button>
          </a>
        </div>
      </div>
    </div>;
}
function HowItWorksSection() {
  const steps = ['Respondes el test y conoces el patrón principal de tu familia.', 'Entras al plan Calma en Casa desde tu celular.', 'Cada día aplicas una microacción simple.', 'Usas las rutinas, frases y checklists en los momentos difíciles.', 'Repites el sistema durante 21 días para crear más calma y estructura.'];
  return <div className="px-5 py-14 bg-white" data-id="element-234">
      <div className="max-w-lg mx-auto" data-id="element-235">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-236">
          ¿Cómo funciona?
        </h2>

        <div className="space-y-4 relative" data-id="element-237">
          <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-100 rounded-full" data-id="element-238" />

          {steps.map((step, i) => <div key={i} className="relative flex items-center" data-id="element-239">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-brand-blue text-white text-card-body font-bold shadow-md shrink-0 z-10 border-3 border-white" data-id="element-240">
                {i + 1}
              </div>
              <div className="ml-4 bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-sm flex-1" data-id="element-241">
                <p className="font-medium text-gray-700 text-card-body" data-id="element-242">
                  {step}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-10 text-center bg-blue-50 p-6 rounded-2xl border border-blue-100" data-id="element-243">
          <p className="font-heading font-bold text-brand-blue mb-4 text-card-title" data-id="element-244">
            No es otra pantalla para tu hijo. Es una guía diaria para los
            padres.
          </p>
          <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="inline-block w-full" data-id="element-245">
            <Button className="w-full" data-id="element-246">Quiero empezar hoy</Button>
          </a>
        </div>
      </div>
    </div>;
}
function TestimonialsSection() {
  return <div className="px-5 py-14 bg-brand-beige/30" data-id="element-247">
      <div className="max-w-lg mx-auto" data-id="element-248">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-249">
          No eres el único padre que se siente así.
        </h2>

        <div className="space-y-4 mb-10" data-id="element-250">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-251">
            <div className="flex text-yellow-400 mb-3" data-id="element-252">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-253" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-254">
              "Por primera vez sentí que tenía un plan, no solo consejos
              sueltos."
            </p>
            <div className="flex items-center space-x-3" data-id="element-255">
              <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center text-brand-blue text-card-body font-bold" data-id="element-256">
                M
              </div>
              <div data-id="element-257">
                <p className="text-card-body font-bold text-gray-700" data-id="element-258">
                  María, 34 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-259">
                  Madre de un niño de 6 años
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-260">
            <div className="flex text-yellow-400 mb-3" data-id="element-261">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-262" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-263">
              "Las frases y rutinas me ayudaron a reaccionar con más calma."
            </p>
            <div className="flex items-center space-x-3" data-id="element-264">
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green text-card-body font-bold" data-id="element-265">
                C
              </div>
              <div data-id="element-266">
                <p className="text-card-body font-bold text-gray-700" data-id="element-267">
                  Carlos, 37 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-268">
                  Padre de una niña de 8 años
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-269">
            <div className="flex text-yellow-400 mb-3" data-id="element-270">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-271" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-272">
              "Me gustó que no era otra pantalla para mi hijo, sino una guía
              para mí."
            </p>
            <div className="flex items-center space-x-3" data-id="element-273">
              <div className="w-10 h-10 rounded-full bg-brand-lavender/20 flex items-center justify-center text-brand-lavender text-card-body font-bold" data-id="element-274">
                A
              </div>
              <div data-id="element-275">
                <p className="text-card-body font-bold text-gray-700" data-id="element-276">
                  Andrea, 31 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-277">
                  Madre de dos niños
                </p>
              </div>
            </div>
          </div>
        </div>

        <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-278">
          <Button variant="success" className="w-full" data-id="element-279">
            Empezar Calma en Casa
          </Button>
        </a>
      </div>
    </div>;
}
function FAQSection() {
  const faqs = [{
    q: '¿Esto es un diagnóstico de TDAH?',
    a: 'No. Este test no diagnostica TDAH ni reemplaza una evaluación médica, psicológica o educativa. Es una orientación práctica para padres que buscan mejorar rutinas, calma y convivencia en casa.'
  }, {
    q: '¿Mi hijo tiene que usar la pantalla?',
    a: 'No. Calma en Casa está pensado para el padre o la madre. No es otra pantalla para tu hijo.'
  }, {
    q: '¿Cuánto tiempo necesito al día?',
    a: 'El plan está diseñado con microacciones de 5 a 10 minutos al día.'
  }, {
    q: '¿Dónde recibo el material?',
    a: 'Después de la compra recibes acceso digital con tu correo para entrar y revisar el material cuando quieras.'
  }, {
    q: '¿Sirve si mi hijo no tiene diagnóstico?',
    a: 'Sí. El plan está enfocado en desafíos cotidianos como rabietas, rutinas difíciles, pantallas y concentración. No necesitas un diagnóstico para aplicar herramientas prácticas en casa.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="px-5 py-14 bg-white" data-id="element-280">
      <div className="max-w-lg mx-auto" data-id="element-281">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-282">
          Preguntas Frecuentes
        </h2>

        <div className="space-y-3 mb-12" data-id="element-283">
          {faqs.map((faq, i) => <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden" data-id="element-284">
              <button className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-285">
                <span className="font-heading font-bold text-brand-dark pr-3 text-card-title" data-id="element-286">
                  {faq.q}
                </span>
                <ChevronDown className={`shrink-0 text-gray-400 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} size={20} data-id="element-287" />
              </button>
              <AnimatePresence data-id="element-288">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-289">
                    <div className="p-4 bg-white text-gray-600 leading-relaxed border-t border-gray-100 text-card-body" data-id="element-290">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-291">
          <Button variant="success" className="w-full" data-id="element-292">
            Sí, quiero empezar el plan de 21 días
          </Button>
        </a>
      </div>
    </div>;
}
function Footer() {
  return <footer className="bg-brand-dark text-gray-400 py-12 px-5" data-id="element-293">
      <div className="max-w-lg mx-auto" data-id="element-294">
        <h3 className="text-white font-heading font-bold text-brand mb-6 text-center" data-id="element-295">
          Calma en Casa
        </h3>

        <div className="mb-8" data-id="element-296">
          <LegalAccordion variant="dark" data-id="element-297" />
        </div>

        <div className="bg-white/5 p-4 rounded-2xl text-disclaimer leading-relaxed text-gray-400 border border-white/10" data-id="element-298">
          <p className="font-bold text-gray-300 mb-1.5 uppercase tracking-wider text-[11px]" data-id="element-299">
            Aviso importante:
          </p>
          <p data-id="element-300">
            Este contenido es educativo y no sustituye atención médica,
            psicológica, terapéutica o educativa profesional. Este test no
            diagnostica TDAH ni ninguna otra condición. Su propósito es orientar
            a padres con herramientas prácticas para mejorar la convivencia, las
            rutinas y la calma en casa.
          </p>
        </div>
      </div>
    </footer>;
}